import { useSyncExternalStore } from "react";

/**
 * 데이터셋별 "검토가 저장된 세대" 번호.
 *
 * 리뷰 독은 미리보기 pane 아래에, 필터 레인은 목록 사이드바에 있다. 둘은 부모-자식이 아니라
 * 형제다. 그리고 사이에 있는 `FileBrowser` 는 리뷰를 모른다. 그래서 모듈 스토어로 연결한다
 * (`annotation/episodeReviewDirtyStore` 와 같은 구조).
 *
 * 저장 내용은 담지 않고 **세대 번호만** 늘린다. 소비처는 "무엇이 바뀌었나" 가 아니라
 * "다시 조회해야 하나" 만 알면 되기 때문이다. 제외·결함·결과 중 어느 축을 고쳐도
 * 필터 개수는 다시 세어야 한다. 스토어가 이 판정을 하면 축이 추가될 때마다 여기도 고쳐야 한다.
 *
 * 창을 여러 개 열 수 있으므로 데이터셋별로 나눈다. 에피소드별 세대도 따로 센다. 진단 표면은 저장한
 * 에피소드의 상세만 다시 받는다. 검토 완료가 그 에피소드의 품질 후보 확인을 기록하기 때문이다.
 */
const revisions = new Map<string, number>();
const episodeRevisions = new Map<string, number>();
const listeners = new Set<() => void>();

function episodeKey(datasetUuid: string, episodeUuid: string): string {
  return `${datasetUuid}/${episodeUuid}`;
}

export const episodeReviewSavedStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  revision(datasetUuid: string): number {
    return revisions.get(datasetUuid) ?? 0;
  },
  episodeRevision(datasetUuid: string, episodeUuid: string): number {
    return episodeRevisions.get(episodeKey(datasetUuid, episodeUuid)) ?? 0;
  },
  /**
   * 검토 저장이 성공했음을 알린다. 데이터셋 세대와 그 에피소드의 세대를 함께 올린다. 번호는 단조
   * 증가하므로 소비처가 "내가 본 세대" 와 비교할 수 있다.
   */
  markSaved(datasetUuid: string, episodeUuid: string): void {
    revisions.set(datasetUuid, (revisions.get(datasetUuid) ?? 0) + 1);
    const key = episodeKey(datasetUuid, episodeUuid);
    episodeRevisions.set(key, (episodeRevisions.get(key) ?? 0) + 1);
    for (const listener of listeners) {
      listener();
    }
  },
};

/**
 * 이 데이터셋에서 검토를 저장한 횟수. 필터 레인은 이 값으로 개수를 다시 받을지 판단한다.
 *
 * 스냅샷이 원시값이므로 `useSyncExternalStore` 가 매번 새 객체를 만들지 않는다.
 */
export function useEpisodeReviewSavedRevision(datasetUuid: string): number {
  return useSyncExternalStore(episodeReviewSavedStore.subscribe, () =>
    episodeReviewSavedStore.revision(datasetUuid),
  );
}

/** 이 에피소드의 검토를 저장한 횟수. 진단 표면은 이 값으로 상세를 다시 받을지 판단한다. */
export function useEpisodeReviewSavedEpisodeRevision(
  datasetUuid: string,
  episodeUuid: string,
): number {
  return useSyncExternalStore(episodeReviewSavedStore.subscribe, () =>
    episodeReviewSavedStore.episodeRevision(datasetUuid, episodeUuid),
  );
}
